import { useRef, useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { API, waLink } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

const TRIP_TYPES = ["One Way", "Round Trip", "Local", "Airport Transfer", "Railway Transfer", "Sightseeing", "Full Day Rental", "Multi-Day Tour"];
const VEHICLES = ["Any", "Sedan", "Dzire", "Ertiga", "Kia Carens", "Innova", "Innova Crysta", "Fortuner", "Luxury Car", "Tempo Traveller", "Urbania", "Luxury Bus"];
const today = () => new Date().toISOString().slice(0, 10);
const PHONE_RE = /^(?:\+?91|0)?[6-9]\d{9}$/;

function validate(f, full) {
  const e = {};
  if (full && f.name.trim().length < 2) e.name = "Please enter your name";
  if (!PHONE_RE.test(f.phone.replace(/[\s-]/g, ""))) e.phone = "Enter a valid 10-digit mobile number";
  if (f.pickup.trim().length < 2) e.pickup = "Pickup location is required";
  if (!f.date) e.date = "Select a travel date";
  else if (f.date < today()) e.date = "Date cannot be in the past";
  if (f.return_date && f.date && f.return_date < f.date) e.return_date = "Return must be after travel date";
  const p = Number(f.passengers);
  if (!p || p < 1 || p > 60) e.passengers = "1 to 60 passengers";
  return e;
}

const waText = (f) =>
  `Hi Lucky Taxi Service, I'd like a fare quote.\nName: ${f.name || "-"}\nPhone: ${f.phone}\nPickup: ${f.pickup}\nDrop: ${f.drop || "-"}\nDate: ${f.date}${f.return_date ? `\nReturn: ${f.return_date}` : ""}\nPassengers: ${f.passengers}\nTrip: ${f.trip_type}\nVehicle: ${f.vehicle}${f.requirement ? `\nNote: ${f.requirement}` : ""}`;

function Field({ id, label, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">{label}</label>
      {children}
      {error && <p id={`${id}-err`} role="alert" className="mt-1 text-[13px] font-medium text-red-700" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

export function BookingForm({ variant = "full", defaults = {}, source = "booking", dark = false, wide = false }) {
  const full = variant === "full";
  const started = useRef(Date.now());
  const [f, setF] = useState({ name: "", phone: "", pickup: defaults.pickup || "", drop: defaults.drop || "", date: "", return_date: "", passengers: 2, trip_type: defaults.trip_type || "One Way", vehicle: defaults.vehicle || "Any", requirement: "", company: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const pid = (k) => `${source}-${k}`;
  const inp = (k, extra = {}) => ({ id: pid(k), value: f[k], onChange: set(k), className: "field", "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${pid(k)}-err` : undefined, "data-testid": `${source}-input-${k}`, ...extra });

  const submit = async (withWa) => {
    const e = validate(f, full);
    setErrors(Object.fromEntries(Object.entries(e).map(([k, v]) => [k, v])));
    if (Object.keys(e).length) { setState("idle"); return; }
    if (withWa) window.open(waLink(waText(f)), "_blank", "noopener");
    setState("loading");
    try {
      const res = await fetch(`${API}/bookings`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...f, passengers: Number(f.passengers), source, elapsed_ms: Date.now() - started.current }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw Object.assign(new Error("req"), { response: { data: j } }); }
      trackEvent("generate_lead", { source });
      setState("success");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg?.replace("Value error, ", "")).join(" ") : "We couldn't send your request. Please call or WhatsApp us.");
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-ink" role="status" data-testid={`${source}-success`}>
        <CheckCircle2 className="h-8 w-8 text-emerald-700" aria-hidden />
        <h3 className="mt-3 text-2xl font-semibold">Request received</h3>
        <p className="mt-2 text-[15px] text-body">Thank you. Lucky Taxi Service will call you on {f.phone} with the current fare for your trip. For a faster reply, send the details on WhatsApp.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href={waLink(waText(f))} target="_blank" rel="noopener noreferrer" className="btn-wa" data-testid={`${source}-success-whatsapp`}><MessageCircle className="h-4 w-4" aria-hidden />Continue on WhatsApp</a>
          <button type="button" onClick={() => { setState("idle"); started.current = Date.now(); }} className="btn-outline" data-testid={`${source}-new-request`}>New request</button>
        </div>
      </div>
    );
  }

  const w = (c) => (wide ? c : "");
  const lbl = dark ? "[&_.field-label]:text-zinc-300" : "";
  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); submit(false); }} className={`relative grid gap-4 ${w("lg:grid-cols-12 lg:items-start")} ${lbl}`} data-testid={`${source}-form`} aria-label="Taxi booking form">
      <input type="text" name="company" tabIndex={-1} autoComplete="off" value={f.company} onChange={set("company")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
      {full && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id={pid("name")} label="Name" error={errors.name}><input {...inp("name", { autoComplete: "name", placeholder: "Your full name" })} /></Field>
          <Field id={pid("phone")} label="Phone" error={errors.phone}><input {...inp("phone", { type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "10-digit mobile" })} /></Field>
        </div>
      )}
      <div className={`grid gap-4 sm:grid-cols-2 ${w("lg:col-span-4")}`}>
        <Field id={pid("pickup")} label="Pickup Location" error={errors.pickup}><input {...inp("pickup", { placeholder: "e.g. Jodhpur Railway Station" })} /></Field>
        <Field id={pid("drop")} label="Drop Location" error={errors.drop}><input {...inp("drop", { placeholder: "e.g. Jaisalmer" })} /></Field>
      </div>
      <div className={`grid grid-cols-2 gap-4 ${w("lg:col-span-3")}`}>
        <Field id={pid("date")} label="Travel Date" error={errors.date}><input {...inp("date", { type: "date", min: today() })} /></Field>
        <Field id={pid("return_date")} label="Return Date" error={errors.return_date}><input {...inp("return_date", { type: "date", min: f.date || today() })} /></Field>
      </div>
      <div className={`grid grid-cols-2 gap-4 sm:grid-cols-3 ${w("lg:col-span-5")}`}>
        <Field id={pid("passengers")} label="Passengers" error={errors.passengers}><input {...inp("passengers", { type: "number", min: 1, max: 60 })} /></Field>
        <Field id={pid("trip_type")} label="Trip Type"><select {...inp("trip_type")}>{TRIP_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
        <div className="col-span-2 sm:col-span-1"><Field id={pid("vehicle")} label={full ? "Vehicle Preference" : "Vehicle"}><select {...inp("vehicle")}>{VEHICLES.map((t) => <option key={t}>{t}</option>)}</select></Field></div>
      </div>
      {!full && <div className={w("lg:col-span-4")}><Field id={pid("phone")} label="Phone Number" error={errors.phone}><input {...inp("phone", { type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "10-digit mobile" })} /></Field></div>}
      {full && <Field id={pid("requirement")} label="Special Requirement"><textarea {...inp("requirement", { rows: 3, maxLength: 600, placeholder: "Child seat, luggage carrier, extra stops, flight/train number…" })} /></Field>}
      {state === "error" && <p role="alert" className={`flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[14px] text-red-800 ${w("lg:col-span-12")}`} data-testid={`${source}-error`}><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />{msg}</p>}
      <div className={`flex flex-wrap gap-3 ${full ? "" : "pt-1"} ${w("lg:col-span-4 lg:pt-[22px]")}`}>
        {full ? (
          <>
            <button type="submit" className="btn-dark" disabled={state === "loading"} data-testid={`${source}-get-fare-button`}>Get Fare</button>
            <button type="button" onClick={() => submit(false)} className="btn-primary" disabled={state === "loading"} data-testid={`${source}-book-now-button`}>{state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}Book Now</button>
            <button type="button" onClick={() => submit(true)} className="btn-wa" disabled={state === "loading"} data-testid={`${source}-whatsapp-button`}><MessageCircle className="h-4 w-4" aria-hidden />WhatsApp</button>
          </>
        ) : (
          <button type="submit" className="btn-primary w-full !py-3.5 text-[15px]" disabled={state === "loading"} data-testid={`${source}-submit-button`}>
            {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}Get Custom Fare<ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <p className={`text-[12.5px] ${dark ? "text-zinc-400" : "text-body"} ${w("lg:col-span-4 lg:pt-[26px]")}`}>Your details are used only to quote and arrange your trip. Fares are confirmed before travel.</p>
    </form>
  );
}
